import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Film, Play } from "lucide-react";
import { ASSETS } from "../../data/content";
import { FadeUp, MaskLineInView } from "./Reveal";

// Placeholder — swap `videoUrl` when the drone footage arrives.
// Supports YouTube/Vimeo iframe or a direct .mp4 tag via <video>.
const videoUrl = "https://www.youtube.com/embed/iJZK8JmF8wg?autoplay=1&mute=1&loop=1&playlist=iJZK8JmF8wg&controls=1&modestbranding=1&rel=0";

export const DroneVideo = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="drone"
      ref={ref}
      data-testid="drone-section"
      className="relative bg-[#052e1e] py-12 lg:py-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <FadeUp>
            <span className="inline-flex items-center gap-2 font-body text-[#ddb128] text-[13px] tracking-[0.3em] uppercase">
              <Film size={14} /> Bird's Eye View
            </span>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="font-body font-light text-[#f7f3e9]/70 text-base sm:text-lg mt-4 max-w-xl leading-relaxed">
              A slow, sweeping look at the farm — the river cutting through, the
              mango orchards, the terraces, and the mountains beyond.
            </p>
          </FadeUp>
        </div>

        <FadeUp>
          <div className="relative mt-12 rounded-[28px] overflow-hidden border border-[#ddb128]/25 bg-[#07412b] aspect-video shadow-[0_40px_80px_-40px_rgba(0,0,0,0.6)]">
            {videoUrl ? (
              videoUrl.endsWith(".mp4") ? (
                <video
                  src={videoUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="h-full w-full object-cover"
                />
              ) : (
                <iframe
                  src={videoUrl}
                  title="The Brook — drone footage"
                  className="h-full w-full"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              )
            ) : (
              <>
                {/* Parallax placeholder using the hero photo, blurred */}
                <motion.img
                  src={ASSETS.hero}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  style={{ y }}
                  className="absolute inset-0 h-full w-full object-cover opacity-40 blur-sm scale-110 will-change-transform"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#052e1e] via-[#052e1e]/50 to-transparent" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <div
                    className="h-20 w-20 rounded-full bg-[#ddb128]/95 flex items-center justify-center shadow-[0_20px_40px_-10px_rgba(221,177,40,0.6)]"
                    data-testid="drone-play-icon"
                  >
                    <Play className="text-[#07412b] ml-1" size={26} fill="#07412b" />
                  </div>
                  <p className="font-display italic text-[#f7f3e9] text-2xl sm:text-3xl mt-6">
                    Drone footage — arriving soon
                  </p>
                  <p className="font-body font-light text-[#f7f3e9]/60 text-sm mt-2">
                    A quiet flight over The Brook, coming to this space.
                  </p>
                </div>
              </>
            )}
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
